import React, { useState, useEffect } from 'react';
import DynamicIsland from '../components/DynamicIsland';
import CurrentTime from '../components/CurrentTime';
import { CourseCard } from '../components/CourseCard';
import Back from '../components/Back';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import HamburgerMenu from '../components/HamburgerNav';
import './CampusBoard.css';
import { faX } from "@fortawesome/free-solid-svg-icons";

function CampusBoard() {
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null); // For modal display
  const [openDropdown, setOpenDropdown] = useState(null); // Tracks which dropdown is open
  const [searchQuery, setSearchQuery] = useState(""); // For search input

  // Toggle function for dropdowns
  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const parseTime = (timeStr) => {
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    if (modifier === 'PM' && hours < 12) hours += 12; // Convert PM to 24-hour time
    if (modifier === 'AM' && hours === 12) hours = 0; // Convert 12 AM to 00 hours

    return { hours, minutes };
  };

  useEffect(() => {
    const fetchAllData = async () => {
      const [coursesResponse, gradesResponse, assignmentsResponse] = await Promise.all([
        fetch('/courses.json'),
        fetch('/grades.json'),
        fetch('/classes.json'),
      ]);

      const coursesData = await coursesResponse.json();
      const gradesData = await gradesResponse.json();
      const assignmentsData = await assignmentsResponse.json();

      const now = new Date();

      const filteredAssignments = {};
      Object.entries(assignmentsData).forEach(([date, assignmentsForDate]) => {
        const validAssignments = assignmentsForDate.filter((assignment) => {
          const { hours, minutes } = parseTime(assignment.time);
          const [year, month, day] = date.split('-').map(Number);
          const dueDateTime = new Date(year, month - 1, day, hours, minutes, 0);

          return dueDateTime > now;
        });

        if (validAssignments.length > 0) {
          filteredAssignments[date] = validAssignments;
        }
      });

      setCourses(coursesData);
      setGrades(gradesData);
      setAssignments(filteredAssignments);
    };

    fetchAllData();
  }, []);

  const handleCourseSelect = (course) => {
    setSelectedCourse(course); // Open modal with course details
  };

  const closeModal = () => {
    setSelectedCourse(null); // Close modal
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchQuery.toLowerCase().trim();

    const matchingCourse = courses.find(
      (course) =>
        course.courseName.toLowerCase().includes(query) ||
        course.courseCode.toLowerCase().includes(query)
    );

    if (matchingCourse) {
      setSelectedCourse(matchingCourse);
      setSearchQuery("");
      return;
    }

    for (const [date, assignmentsForDate] of Object.entries(assignments)) {
      const matchingAssignment = assignmentsForDate.find(
        (assignment) =>
          assignment.assignment.toLowerCase().includes(query) ||
          (assignment.description &&
            assignment.description.toLowerCase().includes(query))
      );

      if (matchingAssignment) {
        const course = courses.find(
          (course) => course.courseName === matchingAssignment.class
        );
        if (course) {
          setSelectedCourse(course);
          setTimeout(() => {
            document
              .getElementById(
                `assignment-${date}-${matchingAssignment.assignment}`
              )
              .scrollIntoView({ behavior: "smooth" });
          }, 300);
        }
        setSearchQuery("");
        return;
      }
    }

    alert("No matching classes or assignments found.");
  };

  return (
    <section className="campus-container" id="campus-container">
      <CurrentTime />
      <DynamicIsland />
      <main className="campusBoard" id="campus-board">
        <header className="header">
          <Back />
          <nav className="navContainer">
            <div className="pageTitle">CampusBoard</div>
            <HamburgerMenu />
            <div className="userSection">
              <button className="iconButton"></button>
            </div>
          </nav>
          <form
          id="search-form"
          className="searchForm"
          onSubmit={(e) => handleSearch(e)}
        >
          <input
            type="search"
            className="searchInput"
            placeholder="Search classes or assignments..."
            aria-label="Search classes or assignments"
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
          />
        </form>
        </header>

        <section className="semesterSection">
          <button>
            <FontAwesomeIcon
              icon={faArrowLeft}
              size="2x"
              style={{ border: 'none' }}
              title="Spring 2024"
            />
          </button>
          <h2>Fall 2024</h2>
          <button>
            <FontAwesomeIcon
              icon={faArrowRight}
              size="2x"
              style={{ justifyContent: 'flex-end', marginLeft: 'auto', border: 'none' }}
              title="Spring 2025"
            />
          </button>
        </section>

        <section className="coursesGrid">
          {courses.map((course, index) => (
            <CourseCard
              key={index}
              imageUrl={course.imageUrl}
              courseCode={course.courseCode}
              courseName={course.courseName}
              instructorName={course.instructorName}
              courseId={course.courseId}
              onClick={() => handleCourseSelect(course)}
            />
          ))}
        </section>

        {selectedCourse && (
          <div
            className="courseModalWrapper"
            onClick={closeModal}
          >
            <div
              className="courseModal"
              onClick={(e) => e.stopPropagation()} 
            >
              <div className="modalContent">
                <button className="closeModal" onClick={closeModal}>
                  <FontAwesomeIcon icon={faX}/>
                </button>
                <div className="course-information">
                  <h2>
                    {selectedCourse.courseName} ({selectedCourse.courseCode})
                  </h2>
                  <p className="instructor-name">
                    <strong>Instructor:</strong> {selectedCourse.instructorName}
                  </p>
                  <p className="course-roster">
                    <a href="#">Roster</a>
                  </p>
                  <p className="course-schedule">
                    <strong>Schedule:</strong> {selectedCourse.schedule}
                  </p>
                  <p className="course-room">
                    <strong>Room:</strong> {selectedCourse.room}
                  </p>
                  <div className="dropdown">
                    <button
                      onClick={() => toggleDropdown('course-content')}
                      className="dropdown-button"
                      style={{color: "black", fontSize: "18px"}}
                    >
                      Course Content
                    </button>
                    <div
                      className={`dropdown-content ${
                        openDropdown === 'course-content' ? 'dropdown-open' : 'dropdown-closed'
                      }`}
                    >
                      <div className="courseExtraInfo">
                        <ul>
                          {selectedCourse.materials &&
                            selectedCourse.materials.map((material, index) => (
                              <li key={index}>
                                <a href="#">
                                  {typeof material === 'object' ? material.name : material}
                                </a>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

<section className="assignmentsSection" id="assignmentsSection">
        <h2 className="assignmentsTitle" id="assignmentsTitle">
          Upcoming Assignments
        </h2>
        {Object.entries(assignments).map(([date, assignmentsForDate]) => (
          <div key={date} className="dateGroup">
            <h3 className="dateTitle">{date}</h3>
            {assignmentsForDate.map((assignment, index) => (
              <article
                key={index}
                id={`assignment-${date}-${assignment.assignment}`}
                className="assignmentCard"
              >
                <h4 className="assignmentClass">{assignment.class}</h4>
                <p className="assignmentDetails">
                  <strong>{assignment.assignment}</strong>
                  <br />
                  Due by: {assignment.time}
                </p>
                {assignment.description && (
                  <p className="assignmentDescription">
                    {assignment.description}
                  </p>
                )}
              </article>
            ))}
          </div>
        ))}
      </section>

        <section className="studentGrades" id="studentGrades">
          <p className="grades">Grades</p>
          <p className="semester">Fall 2024</p>
          <ul>
            {grades.map((grade, index) => (
              <li key={index}>
                <strong>{grade.course}</strong>: {grade.grade}%
              </li>
            ))}
          </ul>
        </section>
      </main>
    </section>
  );
}

export default CampusBoard;
